import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Play, Pause, Square, Plus, RotateCcw, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '@/lib/store';
import { themeColor } from '@/data/samples';
import { usePlayback } from '@/lib/playback';

/**
 * HighlightTimeline — dual-layer waveform editor with organic dynamics,
 * colored highlight segments, and shared playback with Cards / Stack.
 */

function generateWaveform(count) {
  const bars = [];
  let energy = 0.4;
  for (let i = 0; i < count; i++) {
    energy += ((i * 37) % 11) / 100 - 0.05;
    energy = Math.max(0.08, Math.min(1, energy));
    const micro = ((i * 13) % 7) / 35 - 0.1;
    const silence = i % 47 === 0;
    const height = silence ? 8 : energy * 70 + micro * 30 + 10;
    bars.push(Math.max(5, Math.min(95, height)));
  }
  return bars;
}

function formatTime(sec) {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function highlightSpan(h) {
  const start = Math.max(0, Number(h?.startSec) || 0);
  const dur = Math.max(0.5, Number(h?.durationSec) || 0.5);
  return { start, end: start + dur, dur };
}

function resolveTotalSec(run) {
  const fromRun = Number(run?.durationSec) || 0;
  const fromHighlights = (run?.highlights || []).reduce((max, h) => {
    const { end } = highlightSpan(h);
    return Math.max(max, end);
  }, 0);
  const span = Math.max(fromRun, fromHighlights, 1);
  // Small pad so end handles aren't clipped
  return span * 1.02;
}

export default function HighlightTimeline({ run }) {
  const { state, setState } = useStore();
  const playback = usePlayback();
  const [view, setView] = useState('input');
  const [selected, setSelected] = useState(null);
  const [scrubSec, setScrubSec] = useState(0);
  const waveRef = useRef(null);

  const highlights = run.highlights || [];
  const totalSec = useMemo(() => resolveTotalSec(run), [run]);
  const barCount = 200;
  const bars = useMemo(() => generateWaveform(barCount), []);
  const clipTotalSec = useMemo(
    () => Math.round(highlights.reduce((sum, h) => sum + highlightSpan(h).dur, 0)),
    [highlights],
  );

  const activeHighlight = useMemo(() => {
    const id = playback.highlightId || selected || state.activeHighlightId;
    return highlights.find((h) => h.id === id) || highlights[0] || null;
  }, [playback.highlightId, selected, state.activeHighlightId, highlights]);

  const isPlaying = playback.isPlaying && !!playback.highlightId;

  // Playhead follows live playback, otherwise the scrubbed position
  const absoluteSec = useMemo(() => {
    if (isPlaying && activeHighlight && playback.highlightId === activeHighlight.id) {
      return highlightSpan(activeHighlight).start + (playback.currentTime || 0);
    }
    return Math.max(0, Math.min(totalSec, scrubSec));
  }, [isPlaying, activeHighlight, playback.highlightId, playback.currentTime, scrubSec, totalSec]);

  const playProgress = totalSec > 0
    ? Math.max(0, Math.min(100, (absoluteSec / totalSec) * 100))
    : 0;

  // Keep scrub in sync while audio plays
  useEffect(() => {
    if (!isPlaying || !activeHighlight || playback.highlightId !== activeHighlight.id) return;
    setScrubSec(highlightSpan(activeHighlight).start + (playback.currentTime || 0));
  }, [isPlaying, activeHighlight, playback.highlightId, playback.currentTime]);

  const timeMarkers = useMemo(() => {
    let interval = 10;
    if (totalSec > 2400) interval = 600;
    else if (totalSec > 1200) interval = 300;
    else if (totalSec > 600) interval = 120;
    else if (totalSec > 180) interval = 60;
    else if (totalSec > 60) interval = 15;
    const markers = [];
    for (let t = 0; t <= totalSec; t += interval) {
      markers.push({ time: t, pct: (t / totalSec) * 100 });
    }
    return markers;
  }, [totalSec]);

  const getBarSegment = useCallback((barIdx) => {
    const barTime = ((barIdx + 0.5) / barCount) * totalSec;
    // Prefer the highlight that covers this bar; if multiple, pick closest center
    let best = null;
    let bestDist = Infinity;
    for (const h of highlights) {
      const { start, end } = highlightSpan(h);
      if (barTime >= start && barTime <= end) {
        const center = (start + end) / 2;
        const dist = Math.abs(barTime - center);
        if (dist < bestDist) {
          best = h;
          bestDist = dist;
        }
      }
    }
    return best;
  }, [highlights, totalSec]);

  const selectAndPlay = (h) => {
    if (!h) return;
    setSelected(h.id);
    setState({ activeHighlightId: h.id });
    setScrubSec(highlightSpan(h).start);
    playback.toggle(h);
  };

  const onPlayPause = () => {
    if (!activeHighlight) {
      toast.info('Select a highlight to play');
      return;
    }
    selectAndPlay(activeHighlight);
  };

  const onStop = () => {
    playback.stop();
    setScrubSec(0);
  };

  const onAddCut = () => toast.success('Selection mode — click and drag on grey area to create a cut');
  const onReset = () => { setSelected(null); setScrubSec(0); toast.success('Cuts reset to original'); };

  /** Move the white playhead anywhere on the timeline (grey or colored). */
  const movePlayheadToPct = (pct, { playIfHit = false } = {}) => {
    const clamped = Math.max(0, Math.min(100, pct));
    const absSec = (clamped / 100) * totalSec;
    setScrubSec(absSec);

    const hit = highlights.find((h) => {
      const { start, end } = highlightSpan(h);
      return absSec >= start && absSec <= end;
    });

    if (hit && playIfHit) {
      setSelected(hit.id);
      setState({ activeHighlightId: hit.id });
      const offset = Math.max(0, absSec - highlightSpan(hit).start);
      if (playback.highlightId === hit.id && playback.mode === 'clip' && playback.isPlaying) {
        playback.seek(offset);
      } else {
        playback.play(hit, { startAt: offset });
      }
      return;
    }

    if (playback.isPlaying) playback.pause();
    if (hit) {
      setSelected(hit.id);
      setState({ activeHighlightId: hit.id });
    } else {
      setSelected(null);
      setState({ activeHighlightId: null });
    }
  };

  const pctFromClientX = (clientX, el) => {
    const rect = el.getBoundingClientRect();
    if (!rect.width) return 0;
    return ((clientX - rect.left) / rect.width) * 100;
  };

  return (
    <div className="aurora-panel p-5 sm:p-6" data-testid="showcase-timeline-view">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="tab-seg">
          <button
            className={`tab-btn ${view === 'output' ? 'active' : ''}`}
            onClick={() => setView('output')}
            data-testid="timeline-view-output"
          >
            Output
          </button>
          <button
            className={`tab-btn ${view === 'input' ? 'active' : ''}`}
            onClick={() => setView('input')}
            data-testid="timeline-view-input"
          >
            Input
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs inline-flex items-center gap-1.5" onClick={onAddCut}>
            <Plus className="h-3.5 w-3.5" /> Add Cut
          </button>
          <button className="btn-ghost text-xs inline-flex items-center gap-1.5 !border-[rgba(242,112,76,0.3)]" onClick={onReset}>
            <RotateCcw className="h-3.5 w-3.5 text-[var(--coral)]" /> Reset
          </button>
        </div>
      </div>

      <div className="inner-dark p-4 sm:p-5 relative rounded-xl !border !border-white/35">
        <div className="relative h-5 mb-1">
          {timeMarkers.map(({ time, pct }) => (
            <span
              key={time}
              className="absolute font-mono text-[10px] text-white/40 -translate-x-1/2"
              style={{ left: `${pct}%` }}
            >
              {formatTime(time)}
            </span>
          ))}
        </div>

        <div className="relative h-8 mb-1">
          {highlights.map((h) => {
            const c = themeColor(h.theme);
            const { start, end } = highlightSpan(h);
            const centerPct = (((start + end) / 2) / totalSec) * 100;
            const live = playback.highlightId === h.id;
            return (
              <div
                key={`label-${h.id}`}
                className="absolute -translate-x-1/2 flex flex-col items-center z-10"
                style={{ left: `${Math.max(4, Math.min(96, centerPct))}%` }}
              >
                <button
                  type="button"
                  className="glass-panel-soft !rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider whitespace-nowrap cursor-pointer hover:brightness-125 transition"
                  style={{
                    color: c,
                    borderColor: `${c}40`,
                    boxShadow: live && playback.isPlaying ? `0 0 10px ${c}55` : undefined,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAndPlay(h);
                  }}
                >
                  {h.themeName} <span className="opacity-60">▾</span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="relative h-3">
          {highlights.map((h) => {
            const c = themeColor(h.theme);
            const { start, end } = highlightSpan(h);
            const centerPct = (((start + end) / 2) / totalSec) * 100;
            return (
              <div
                key={`line-${h.id}`}
                className="absolute w-px h-full -translate-x-1/2"
                style={{ left: `${Math.max(4, Math.min(96, centerPct))}%`, background: `${c}50` }}
              />
            );
          })}
        </div>

        <div
          ref={waveRef}
          className="relative h-28 sm:h-36 flex items-end gap-[1.5px] cursor-crosshair"
          onClick={(e) => {
            // Click anywhere on the waveform track (incl. empty padding) moves the playhead
            if (!waveRef.current) return;
            const pct = pctFromClientX(e.clientX, waveRef.current);
            const barEl = e.target?.closest?.('[data-bar]');
            const isHighlight = barEl?.dataset?.highlight === '1';
            movePlayheadToPct(pct, { playIfHit: !!isHighlight });
          }}
        >
          <div
            className="absolute top-0 bottom-0 w-[2px] z-20 pointer-events-none"
            style={{
              left: `${playProgress}%`,
              background: 'linear-gradient(180deg, transparent, white 20%, white 80%, transparent)',
              opacity: 0.95,
              boxShadow: isPlaying ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
            }}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-transparent border-t-white" />
          </div>

          {bars.map((h, i) => {
            const segment = getBarSegment(i);
            const isHighlight = !!segment;
            const c = isHighlight ? themeColor(segment.theme) : null;
            const isSel = isHighlight && (selected === segment.id || playback.highlightId === segment.id);
            const isSync = isHighlight && (state.activeHighlightId === segment.id || playback.highlightId === segment.id);
            const barPct = ((i + 0.5) / barCount) * 100;
            const played = barPct <= playProgress;

            if (view === 'output' && !isHighlight) {
              return <span key={i} className="flex-1 pointer-events-none" style={{ height: '0%' }} />;
            }

            const displayHeight = isHighlight ? Math.min(98, h * 1.25) : h;
            const liveBoost = isHighlight && isSync && playback.isPlaying && played
              ? 1 + 0.12 * Math.sin((playback.progress * 20 + i) * 0.8)
              : 1;

            return (
              <span
                key={i}
                data-bar="1"
                data-highlight={isHighlight ? '1' : '0'}
                className="flex-1 rounded-t-sm transition-all cursor-crosshair"
                style={{
                  height: `${Math.min(100, displayHeight * liveBoost)}%`,
                  background: isHighlight
                    ? `linear-gradient(180deg, ${c} 0%, ${c}bb 100%)`
                    : 'rgba(255,255,255,0.18)',
                  opacity: isHighlight
                    ? (isSel || isSync ? 1 : 0.85)
                    : (played ? 0.85 : 0.7),
                  boxShadow: isHighlight ? `0 -4px 12px -4px ${c}40` : 'none',
                  transform: isSel ? 'scaleY(1.05)' : 'scaleY(1)',
                  transformOrigin: 'bottom',
                }}
                onMouseEnter={() => {
                  if (isHighlight) setState({ activeHighlightId: segment.id });
                }}
                onMouseLeave={() => setState({ activeHighlightId: null })}
              />
            );
          })}

          {highlights.map((h) => {
            const c = themeColor(h.theme);
            const { start, end } = highlightSpan(h);
            const startPct = (start / totalSec) * 100;
            const endPct = (end / totalSec) * 100;
            const isSel = selected === h.id || playback.highlightId === h.id;
            return (
              <React.Fragment key={`handles-${h.id}`}>
                <div
                  className="absolute top-0 bottom-0 z-10 -translate-x-1/2 cursor-pointer group"
                  style={{ left: `${startPct}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAndPlay(h);
                  }}
                >
                  <div className="w-[3px] h-full absolute left-1/2 -translate-x-1/2" style={{ background: isSel ? c : `${c}80` }} />
                  <div
                    className="w-3.5 h-3.5 rounded-full border-2 absolute -top-1 left-1/2 -translate-x-1/2 transition-transform group-hover:scale-125"
                    style={{
                      borderColor: c,
                      background: 'var(--panel-inner)',
                      boxShadow: `0 0 6px ${c}60`,
                    }}
                  />
                </div>
                <div
                  className="absolute top-0 bottom-0 z-10 -translate-x-1/2 cursor-pointer group"
                  style={{ left: `${endPct}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAndPlay(h);
                  }}
                >
                  <div className="w-[3px] h-full absolute left-1/2 -translate-x-1/2" style={{ background: isSel ? c : `${c}80` }} />
                  <div
                    className="w-3.5 h-3.5 rounded-full border-2 absolute -top-1 left-1/2 -translate-x-1/2 transition-transform group-hover:scale-125"
                    style={{
                      borderColor: c,
                      background: 'var(--panel-inner)',
                      boxShadow: `0 0 6px ${c}60`,
                    }}
                  />
                </div>
              </React.Fragment>
            );
          })}

          <div className="absolute bottom-0 left-0 right-0 h-px bg-white/8 pointer-events-none" />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3 glass-panel-soft !rounded-full px-4 py-2.5">
        <button
          type="button"
          className="h-10 w-10 rounded-full flex items-center justify-center transition hover:brightness-125"
          style={{ background: 'var(--brand-grad)' }}
          onClick={onPlayPause}
          data-testid="timeline-play-pause"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying
            ? <Pause className="h-4 w-4 text-[#04121E]" fill="currentColor" />
            : <Play className="h-4 w-4 text-[#04121E] ml-0.5" fill="currentColor" />
          }
        </button>
        <button
          type="button"
          className="h-8 w-8 rounded-full flex items-center justify-center bg-white/5 border border-white/8 hover:bg-white/10 transition"
          onClick={onStop}
          data-testid="timeline-stop"
          aria-label="Stop"
        >
          <Square className="h-3 w-3" />
        </button>
        <span className="font-mono text-xs text-white/55 tabular-nums min-w-[90px]">
          {formatTime(Math.min(absoluteSec, totalSec))} / {formatTime(totalSec)}
        </span>
        <div
          className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden cursor-pointer relative"
          onClick={(e) => {
            const pct = pctFromClientX(e.clientX, e.currentTarget);
            movePlayheadToPct(pct, { playIfHit: true });
          }}
        >
          <div
            className="h-full rounded-full transition-all duration-75"
            style={{ width: `${playProgress}%`, background: 'linear-gradient(90deg, #20B8F2, #7EE06D)' }}
          />
        </div>
        <Volume2 className="h-4 w-4 text-white/40 cursor-pointer" onClick={() => toast.info('Volume control coming soon')} />
        <div className="flex items-center gap-1 ml-1">
          <button type="button" className="text-white/40 hover:text-white/80 text-sm font-mono" onClick={() => toast.info('Zoom out')}>−</button>
          <button type="button" className="text-white/40 hover:text-white/80 text-sm font-mono" onClick={() => toast.info('Zoom in')}>+</button>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/40">
        <span>
          Timeline · {activeHighlight ? activeHighlight.themeName : 'Full Recording'}
          {playback.highlightId ? ` · clip ${playback.clock}` : ''}
        </span>
        <span>{highlights.length} highlights · {run.totalClipTime || `${clipTotalSec}s`} clips</span>
      </div>
    </div>
  );
}
